import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, useCart, useToast } from '@kss/ui';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import {
  FREE_SHIPPING,
  publicCatalogService,
  shippingFor,
  type PublicShipping,
} from '@kss/api';
import { encodeId } from '../utils/idCodec';

export default function CartPage() {
  const cart = useCart();
  const toast = useToast();
  const navigate = useNavigate();

  /* Delivery terms from the tenant's default store. Starts at
     FREE_SHIPPING so the first paint never quotes a fee the merchant
     hasn't configured — the previous hardcoded ₹99 was charged before
     anyone had a chance to set it. */
  const [shippingTerms, setShippingTerms] = useState<PublicShipping>(FREE_SHIPPING);
  useEffect(() => {
    let cancelled = false;
    publicCatalogService.shipping().then((t) => {
      if (!cancelled) setShippingTerms(t);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (cart.items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface-card-hover">
          <ShoppingBag className="h-8 w-8 text-content-muted" />
        </div>
        <h1 className="text-2xl font-bold">Your cart is empty</h1>
        <p className="mt-2 text-content-secondary">Browse products and add items to your cart.</p>
        <Link to="/shop" className="mt-4 inline-block">
          <Button>Go to shop</Button>
        </Link>
      </div>
    );
  }

  const shipping = shippingFor(cart.subtotal, shippingTerms);
  const total = cart.subtotal + shipping;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="mb-6 text-xl font-bold tracking-tight sm:text-2xl">Your cart</h1>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-3">
          {cart.items.map((i) => (
            <div
              key={i.product.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-card p-3 sm:flex-nowrap sm:gap-4"
            >
              {/* Product thumbnail — was an empty colored placeholder
                  div before; now renders the real image with an
                  onError fallback to the brand "K" mark. Bug ref:
                  AI Agent — "Product images are not displaying on
                  the cart page". */}
              <div
                className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border"
                style={{ background: i.product.imageBg || 'var(--color-bg-card-hover)' }}
              >
                {i.product.image ? (
                  <img
                    src={i.product.image}
                    alt={i.product.title}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span
                    aria-label="Image unavailable"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground"
                  >
                    K
                  </span>
                )}
              </div>
              <div className="flex-1">
                <Link
                  to={`/shop/${encodeId(i.product.id)}`}
                  className="font-medium text-content-primary hover:text-primary"
                >
                  {i.product.title}
                </Link>
                <div className="mt-1 text-xs text-content-muted">{i.product.category}</div>
                <div className="mt-1 text-sm font-semibold">
                  ₹{i.product.price.toLocaleString('en-IN')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center overflow-hidden rounded-md border border-border">
                  <button
                    type="button"
                    onClick={() => cart.setQty(i.product.id, i.qty - 1)}
                    className="flex h-8 w-8 items-center justify-center text-content-secondary hover:bg-surface-card-hover"
                    aria-label="Decrease"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-8 text-center text-sm font-semibold">{i.qty}</span>
                  <button
                    type="button"
                    onClick={() => cart.setQty(i.product.id, i.qty + 1)}
                    className="flex h-8 w-8 items-center justify-center text-content-secondary hover:bg-surface-card-hover"
                    aria-label="Increase"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    cart.remove(i.product.id);
                    toast.info('Removed', i.product.title);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-danger hover:bg-danger-light"
                  aria-label="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <aside className="h-fit rounded-lg border border-border bg-surface-card p-4">
          <div className="text-sm font-semibold">Order summary</div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-content-secondary">Subtotal ({cart.count} items)</span>
              <span>₹{cart.subtotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-secondary">Shipping</span>
              <span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
            </div>
            <div className="my-2 border-t border-border" />
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <Button
            fullWidth
            className="mt-4"
            onClick={() => navigate('/checkout')}
          >
            Checkout
          </Button>
          <button
            type="button"
            onClick={() => {
              cart.clear();
              toast.info('Cart cleared');
            }}
            className="mt-2 w-full text-center text-xs text-content-muted hover:text-danger"
          >
            Clear cart
          </button>
        </aside>
      </div>
    </div>
  );
}
